import { useMount } from 'ahooks';
import { Tooltip } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { RevxSkeleton } from 'components';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxTrackingInfo } from 'components/RevxTrackingInfo';
import { UnderlineText } from 'components/RevxTypography';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import {
  FileItem,
  ProofItem,
  ProofStatusEnum,
  ProofTab,
  ProofTypeEnum,
} from 'domains/workspaceDetailDomain';
import {
  JobItem,
  JobParticipantEnum,
  JobTypeEnum,
  JobWorkflowStatusEnum,
} from 'domains/workspaceDomain';
import { useUser } from 'hooks';
import { useJobStatusCollection } from 'hooks/useJobStatusCollection';
import { useOrgId } from 'hooks/useOrg';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { isSupplier } from 'utils/rbac';
import { revxScrollTo } from 'utils/scroll';
import { CommonProofNotes, CommonUploadFile } from '../../common';
import { ProofsOpenNote } from '../../common/ProofNotes';
import './SupplierProof.less';

const SupplierProof = (jobData: JobItem) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useUser();
  const orgId = useOrgId();
  const [proofFileList, setProofFileList] = useState<FileItem[]>([]);
  const [defaultFileList, setDefaultFileList] = useState<FileItem[]>();
  const [proofList, setProofList] = useState<ProofItem[]>([]);
  const [choiceProof, setChoiceProof] = useState<ProofItem>();
  const [trackingCode, setTrackingCode] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeniedApprovalModal, setIsDeniedApprovalModal] =
    useState<boolean>(false);
  const statusFilters = useJobStatusCollection();
  const commonNoteRef = useRef<ProofsOpenNote>(null);

  const [viewNotes, setViewNotes] = useState<boolean>(
    jobData.participants.includes(JobParticipantEnum.CUSTOMER) ||
      jobData.participants.includes(JobParticipantEnum.PROXY)
  );

  const proofTabList: Partial<ProofTab> = useMemo(() => {
    const tabList: Partial<ProofTab> = {};
    tabList[ProofTypeEnum.DIGITAL] = proofList
      .filter((item) => item.proofType === ProofTypeEnum.DIGITAL)
      .reverse();
    tabList[ProofTypeEnum.HARD_COPY] = proofList
      .filter((item) => item.proofType === ProofTypeEnum.HARD_COPY)
      .reverse();
    if (tabList[ProofTypeEnum.DIGITAL]?.length) {
      setChoiceProof(tabList[ProofTypeEnum.DIGITAL]?.slice(0)[0]);
      setTrackingCode(
        tabList[ProofTypeEnum.DIGITAL]?.slice(0)[0]?.trackingCode || ''
      );
      if (
        Boolean(tabList[ProofTypeEnum.DIGITAL]?.slice(0)[0]?.proofMsg?.length)
      ) {
        setViewNotes(true);
      } else {
        setViewNotes(false);
      }
    } else {
      setChoiceProof(tabList[ProofTypeEnum.HARD_COPY]?.slice(0)[0]);
      setTrackingCode(
        tabList[ProofTypeEnum.HARD_COPY]?.slice(0)[0]?.trackingCode || ''
      );
      if (
        Boolean(tabList[ProofTypeEnum.HARD_COPY]?.slice(0)[0]?.proofMsg?.length)
      ) {
        setViewNotes(true);
      } else {
        setViewNotes(false);
      }
    }
    return tabList;
  }, [proofList]);

  const showProofFile = useMemo(() => {
    return (
      defaultFileList?.length ||
      choiceProof?.proofStatus === ProofStatusEnum.DRAFT ||
      choiceProof?.proofStatus === ProofStatusEnum.WAITING_APPROVE
    );
  }, [defaultFileList, choiceProof]);

  const showAddNoteBtn = () => {
    if (jobData.jobType === JobTypeEnum.INTERNAL) {
      return choiceProof?.proofStatus !== ProofStatusEnum.WAITING_APPROVE;
    }
    return true;
  };

  const proofActionStatus = useMemo(() => {
    const choiceFileDisabled =
      choiceProof?.proofStatus !== ProofStatusEnum.WAITING_APPROVE;
    const showProofActionApprove =
      choiceProof?.proofStatus === ProofStatusEnum.APPROVE;
    const showProofActionDecline =
      choiceProof?.proofStatus === ProofStatusEnum.DECLINED;
    return {
      choiceFileDisabled,
      showProofActionApprove,
      showProofActionDecline,
      proofActionFinish: showProofActionApprove || showProofActionDecline,
    };
  }, [choiceProof]);

  useEffect(() => {
    if (isDeniedApprovalModal) {
      setViewNotes(true);
    }
  }, [isDeniedApprovalModal]);

  useEffect(() => {
    if (!viewNotes) {
      setIsDeniedApprovalModal(false);
    }
  }, [viewNotes]);

  const openNotes = (e: React.MouseEvent<HTMLElement>) => {
    setIsDeniedApprovalModal(true);
    commonNoteRef.current?.openNoteHandler();
    revxScrollTo(e.pageY - 200);
  };

  const declineHandle = async () => {
    choiceProof && (await dispatch.workspaceDetail.buyerDecline(choiceProof));
    defaultProofList();
  };

  const approveHandle = async () => {
    choiceProof && (await dispatch.workspaceDetail.buyerApprove(choiceProof));
    defaultProofList();
    dispatch.workspace.getJobCountByStatusFilter(statusFilters);
    dispatch.workspace.reloadJobList();
  };

  const showRequestApproval = useMemo(() => {
    const hideProofType = choiceProof?.proofStatus === ProofStatusEnum.DRAFT;
    const hideRole = isSupplier(user);
    const hideOrg = orgId === jobData.supplierOrgId;
    let disableFileList;
    if (proofTabList[ProofTypeEnum.HARD_COPY]?.length) {
      if (jobData.digitalProofRequired && jobData.hardcopyProofRequired) {
        disableFileList = !choiceProof?.trackingCode || !proofFileList.length;
      } else {
        disableFileList = !choiceProof?.trackingCode;
      }
    } else {
      disableFileList = !proofFileList.length;
    }
    return {
      accessControl: hideProofType && hideRole && hideOrg,
      DefControl: disableFileList,
    };
  }, [choiceProof, user, jobData, proofFileList, proofTabList, orgId]);

  const showPrepareNextVersion: boolean = useMemo(() => {
    const hideProofType = choiceProof?.proofStatus === ProofStatusEnum.DECLINED;
    const hideRole = isSupplier(user);
    const hideOrg = orgId === jobData.supplierOrgId;
    let lastVersion =
      choiceProof?.id === proofTabList[ProofTypeEnum.DIGITAL]?.slice(0)[0]?.id;
    if (proofTabList[ProofTypeEnum.HARD_COPY]?.length) {
      lastVersion =
        choiceProof?.id ===
        proofTabList[ProofTypeEnum.HARD_COPY]?.slice(0)[0]?.id;
    }
    return hideProofType && hideRole && hideOrg && lastVersion;
  }, [choiceProof, user, jobData, proofTabList, orgId]);

  const defaultProofList = useCallback(async () => {
    setLoading(true);
    const proofListRes = await dispatch.workspaceDetail.getProofAll(jobData);
    setProofList(proofListRes);
    setLoading(false);
    return proofListRes;
  }, [dispatch.workspaceDetail, jobData]);

  useMount(async () => {
    if (jobData.digitalProofRequired || jobData.hardcopyProofRequired) {
      defaultProofList();
      dispatch.workspace.getJobItemCount(jobData.id);
    }
  });

  const defaultUpdateJobItem = useCallback(async () => {
    dispatch.workspace.getJobItemCount(jobData.id);
  }, [dispatch.workspace, jobData]);

  const getUpdateProofFile = useCallback(async () => {
    if (choiceProof) {
      const fileListRes = await dispatch.workspaceDetail.getAllFileProof(
        choiceProof
      );
      setProofFileList(fileListRes);
    }
  }, [choiceProof, dispatch.workspaceDetail]);

  const getDefaultProofFile = useCallback(async () => {
    if (choiceProof) {
      const fileListRes = await dispatch.workspaceDetail.getAllFileProof(
        choiceProof
      );
      setDefaultFileList(fileListRes);
      setProofFileList(fileListRes);
    }
  }, [choiceProof, dispatch.workspaceDetail]);

  useEffect(() => {
    getDefaultProofFile();
  }, [choiceProof, getDefaultProofFile]);

  const requestApproval = async () => {
    setLoading(true);
    choiceProof &&
      (await dispatch.workspaceDetail.supplierApproval(choiceProof));
    defaultProofList();
    setLoading(false);
    defaultUpdateJobItem();
  };

  const prepareNextVersion = async () => {
    setLoading(true);
    choiceProof &&
      (await dispatch.workspaceDetail.nextProofVersion(choiceProof));
    defaultProofList();
    setLoading(false);
    defaultUpdateJobItem();
  };

  const onUploadFileChange = async () => {
    getUpdateProofFile();
  };

  const digitalProofRender = () => (
    <>
      <p className="buyer-proof-card-approve-title">
        {t('workspace.detail.proofs.digitalProofApproval')}
      </p>
      {!proofActionStatus.showProofActionApprove && (
        <div
          className="buyer-proof-card-shipping"
          style={{ borderBottom: 'none' }}
        >
          <span hidden={ProofStatusEnum.DRAFT === choiceProof?.proofStatus}>
            {t('workspace.detail.proofs.edits')}:
            {`  (${choiceProof?.proofMsg.length})`}
          </span>
          <Tooltip title={choiceProof?.proofMsg[0]?.msg}>
            <UnderlineText
              color="blue"
              size="small"
              style={{ paddingLeft: 10 }}
              ellipsis
              onClick={() => setViewNotes(!viewNotes)}
            >
              {t('workspace.detail.proofs.viewNotes')}
            </UnderlineText>
          </Tooltip>
        </div>
      )}

      <RevxFlexLayout align="top">
        <p
          className="buyer-proof-card-approve-title"
          hidden={proofTabList[ProofTypeEnum.DIGITAL]?.length === 1}
        >
          {t('workspace.detail.proofs.proof')}:
        </p>
        <p
          className="buyer-proof-card-approve-title"
          style={{ lineHeight: '40px' }}
          hidden={proofTabList[ProofTypeEnum.DIGITAL]?.length !== 1}
        >
          {t('workspace.detail.proofs.proof')}:
        </p>

        <div>
          <p
            className="supplier-proof-card-version"
            hidden={proofTabList[ProofTypeEnum.DIGITAL]?.length === 1}
          >
            {proofTabList[ProofTypeEnum.DIGITAL]?.map((item) => (
              <React.Fragment key={item.id}>
                {choiceProof?.id !== item.id ? (
                  <a
                    onClick={() => {
                      setChoiceProof(item);
                      setDefaultFileList([]);
                      setProofFileList([]);
                      setTrackingCode(item?.trackingCode || '');
                      setViewNotes(false);
                    }}
                  >
                    {`Version ${item.proofVersion}`}
                  </a>
                ) : (
                  <span>{`Version ${choiceProof?.proofVersion}`}</span>
                )}
              </React.Fragment>
            ))}
          </p>
          {Boolean(
            choiceProof?.proofStatus === ProofStatusEnum.DRAFT ||
              choiceProof?.proofStatus === ProofStatusEnum.WAITING_APPROVE
          ) ? (
            <div
              className="buyer-proof-card-shipping"
              style={{ borderBottom: 'none', marginBottom: `-24px` }}
            >
              <span>{t('workspace.detail.proofs.addDigitalProof')}:</span>
            </div>
          ) : (
            <></>
          )}
          <CommonUploadFile
            showUploadButton={
              choiceProof?.proofStatus === ProofStatusEnum.DRAFT ||
              choiceProof?.proofStatus === ProofStatusEnum.WAITING_APPROVE
            }
            itemData={choiceProof}
            defaultFileList={defaultFileList}
            deleteFileHandler={
              Boolean((proofFileList || []).length > 1)
                ? dispatch.workspaceDetail.deleteProofFile
                : undefined
            }
            getAllFileHandler={dispatch.workspaceDetail.getAllFileProof}
            saveFileHandler={dispatch.workspaceDetail.saveProofFile}
            onUploadFileChange={onUploadFileChange}
            setIsUploading={setIsUploading}
          />
        </div>
      </RevxFlexLayout>
    </>
  );

  const hardcopyProofRender = () => (
    <>
      <p className="buyer-proof-card-approve-title">
        {t('workspace.detail.proofs.hardcopyProof')}
      </p>
      <RevxFlexLayout align="top">
        <div>
          <p
            className="buyer-proof-card-approve-title"
            hidden={proofTabList[ProofTypeEnum.HARD_COPY]?.length === 1}
          >
            {t('workspace.detail.proofs.proof')}:
          </p>
          <p
            className="buyer-proof-card-approve-title"
            style={{ lineHeight: '44px' }}
            hidden={proofTabList[ProofTypeEnum.HARD_COPY]?.length !== 1}
          >
            {t('workspace.detail.proofs.proof')}:
          </p>
        </div>
        <div>
          <p
            className="supplier-proof-card-version"
            hidden={proofTabList[ProofTypeEnum.HARD_COPY]?.length === 1}
          >
            {/* <span>{t('workspace.detail.proofs.proof')}:</span> */}
            {proofTabList[ProofTypeEnum.HARD_COPY]?.map((item) => (
              <React.Fragment key={item.id}>
                {choiceProof?.id !== item.id ? (
                  <a
                    onClick={() => {
                      setChoiceProof(item);
                      setDefaultFileList([]);
                      setProofFileList([]);
                      setTrackingCode(item?.trackingCode || '');
                      setViewNotes(false);
                    }}
                  >
                    {`Version ${item.proofVersion}`}
                  </a>
                ) : (
                  <span>{`Version ${item.proofVersion}`}</span>
                )}
              </React.Fragment>
            ))}
          </p>
          <div className="supplier-proof-card-address">
            <span>{t('workspace.detail.proofs.supplierSendProofTo')}:</span>
            {[
              choiceProof?.toName,
              choiceProof?.toAddressLine,
              `${choiceProof?.toCity}, ${choiceProof?.toStateProvinceCode}, ${choiceProof?.toPostalCode}`,
            ]
              .filter((item) => item)
              .map((item) => (
                <p key={item}>{item}</p>
              ))}
          </div>
          <div
            className={`supplier-proof-card-shipping ${
              !showProofFile ? 'supplier-proof-card-shipping-hide' : ''
            }`}
            style={
              choiceProof?.proofStatus === ProofStatusEnum.DRAFT ||
              choiceProof?.proofStatus === ProofStatusEnum.WAITING_APPROVE
                ? { padding: '8px 0' }
                : {}
            }
          >
            <span style={{ paddingRight: 8 }}>
              {t('workspace.detail.proofs.shippingTracking')}:
            </span>
            <RevxTrackingInfo
              carrierType={'UPS' as CarrierTypeEnum}
              editable={
                choiceProof?.proofStatus === ProofStatusEnum.DRAFT ||
                choiceProof?.proofStatus === ProofStatusEnum.WAITING_APPROVE
              }
              size="default"
              defaultEmptyEditing
              value={trackingCode}
              onChange={async (val) => {
                if (choiceProof) {
                  setTrackingCode(val);
                  await dispatch.workspaceDetail.shippingNumber({
                    ...choiceProof,
                    trackingCode: val,
                  });
                  defaultProofList();
                }
              }}
            />
            <span hidden={ProofStatusEnum.DRAFT === choiceProof?.proofStatus}>
              {t('workspace.detail.proofs.editsReceived')}:
              {ProofStatusEnum.DRAFT !== choiceProof?.proofStatus
                ? `  (${choiceProof?.proofMsg.length})`
                : ''}
            </span>
            {Boolean(choiceProof?.proofMsg.length) ||
            (choiceProof?.proofStatus &&
              [ProofStatusEnum.WAITING_APPROVE].includes(
                choiceProof?.proofStatus
              )) ? (
              <>
                <Tooltip title={choiceProof?.proofMsg[0]?.msg}>
                  <UnderlineText
                    color="blue"
                    size="small"
                    style={{ paddingLeft: 10 }}
                    ellipsis
                    onClick={() => setViewNotes(!viewNotes)}
                  >
                    {t('workspace.detail.proofs.viewNotes')}
                  </UnderlineText>
                </Tooltip>
              </>
            ) : (
              <></>
            )}
          </div>
        </div>
      </RevxFlexLayout>
    </>
  );

  const digitalHardcopyProofRender = () => (
    <>
      <p className="buyer-proof-card-approve-title">
        {t('workspace.detail.proofs.digitalHardcopyProof')}
      </p>
      <RevxFlexLayout align="top">
        <div>
          <p
            className="buyer-proof-card-approve-title"
            hidden={proofTabList[ProofTypeEnum.HARD_COPY]?.length === 1}
          >
            {t('workspace.detail.proofs.proof')}:
          </p>
          <p
            className="buyer-proof-card-approve-title"
            style={{ lineHeight: '44px' }}
            hidden={proofTabList[ProofTypeEnum.HARD_COPY]?.length !== 1}
          >
            {t('workspace.detail.proofs.proof')}:
          </p>
        </div>
        <div>
          <p
            className="supplier-proof-card-version"
            hidden={proofTabList[ProofTypeEnum.HARD_COPY]?.length === 1}
          >
            {/* <span>{t('workspace.detail.proofs.proof')}:</span> */}
            {proofTabList[ProofTypeEnum.HARD_COPY]?.map((item) => (
              <React.Fragment key={item.id}>
                {choiceProof?.id !== item.id ? (
                  <a
                    onClick={() => {
                      setChoiceProof(item);
                      setDefaultFileList([]);
                      setProofFileList([]);
                      setTrackingCode(item?.trackingCode || '');
                      setViewNotes(false);
                    }}
                  >
                    {`Version ${item.proofVersion}`}
                  </a>
                ) : (
                  <span>{`Version ${item.proofVersion}`}</span>
                )}
              </React.Fragment>
            ))}
          </p>
          <div className="supplier-proof-card-address">
            <span>{t('workspace.detail.proofs.supplierSendProofTo')}:</span>
            {[
              choiceProof?.toName,
              choiceProof?.toAddressLine,
              `${choiceProof?.toCity}, ${choiceProof?.toStateProvinceCode}, ${choiceProof?.toPostalCode}`,
            ]
              .filter((item) => item)
              .map((item) => (
                <p key={item}>{item}</p>
              ))}
          </div>
          <div
            className={`supplier-proof-card-shipping ${
              !showProofFile ? 'supplier-proof-card-shipping-hide' : ''
            }`}
            style={
              choiceProof?.proofStatus === ProofStatusEnum.DRAFT ||
              choiceProof?.proofStatus === ProofStatusEnum.WAITING_APPROVE
                ? { padding: '8px 0' }
                : {}
            }
          >
            <span style={{ paddingRight: 8 }}>
              {t('workspace.detail.proofs.shippingTracking')}:
            </span>
            <RevxTrackingInfo
              carrierType={'UPS' as CarrierTypeEnum}
              editable={
                choiceProof?.proofStatus === ProofStatusEnum.DRAFT ||
                choiceProof?.proofStatus === ProofStatusEnum.WAITING_APPROVE
              }
              size="default"
              defaultEmptyEditing
              value={trackingCode}
              onChange={async (val) => {
                if (choiceProof) {
                  setTrackingCode(val);
                  await dispatch.workspaceDetail.shippingNumber({
                    ...choiceProof,
                    trackingCode: val,
                  });
                  defaultProofList();
                }
              }}
            />
            <span hidden={ProofStatusEnum.DRAFT === choiceProof?.proofStatus}>
              {t('workspace.detail.proofs.editsReceived')}:
              {ProofStatusEnum.DRAFT !== choiceProof?.proofStatus
                ? `  (${choiceProof?.proofMsg.length})`
                : ''}
            </span>
            {Boolean(choiceProof?.proofMsg.length) ||
            (choiceProof?.proofStatus &&
              [ProofStatusEnum.WAITING_APPROVE].includes(
                choiceProof?.proofStatus
              )) ? (
              <>
                <Tooltip title={choiceProof?.proofMsg[0]?.msg}>
                  <UnderlineText
                    color="blue"
                    size="small"
                    style={{ paddingLeft: 10 }}
                    ellipsis
                    onClick={() => setViewNotes(!viewNotes)}
                  >
                    {t('workspace.detail.proofs.viewNotes')}
                  </UnderlineText>
                </Tooltip>
              </>
            ) : (
              <></>
            )}
          </div>
          {Boolean(
            choiceProof?.proofStatus === ProofStatusEnum.DRAFT ||
              choiceProof?.proofStatus === ProofStatusEnum.WAITING_APPROVE
          ) ? (
            <div
              className="buyer-proof-card-shipping"
              style={{ borderBottom: 'none', marginBottom: `-24px` }}
            >
              <span>{t('workspace.detail.proofs.addDigitalProof')}:</span>
            </div>
          ) : (
            <></>
          )}
          {showProofFile && (
            <CommonUploadFile
              showUploadButton={
                choiceProof?.proofStatus === ProofStatusEnum.DRAFT ||
                choiceProof?.proofStatus === ProofStatusEnum.WAITING_APPROVE
              }
              itemData={
                choiceProof && {
                  ...choiceProof,
                  ...{ skipArtFile: jobData.skipArtFile },
                }
              }
              defaultFileList={defaultFileList}
              deleteFileHandler={
                Boolean((proofFileList || []).length > 1)
                  ? dispatch.workspaceDetail.deleteProofFile
                  : undefined
              }
              getAllFileHandler={dispatch.workspaceDetail.getAllFileProof}
              saveFileHandler={dispatch.workspaceDetail.saveProofFile}
              onUploadFileChange={onUploadFileChange}
              setIsUploading={setIsUploading}
            />
          )}
        </div>
      </RevxFlexLayout>
    </>
  );

  return (
    <div className="supplier-proof-card">
      {jobData.digitalProofRequired || jobData.hardcopyProofRequired ? (
        <RevxSkeleton loading={loading}>
          {Boolean(proofTabList[ProofTypeEnum.DIGITAL]?.length) &&
            digitalProofRender()}
          {Boolean(proofTabList[ProofTypeEnum.HARD_COPY]?.length) ? (
            jobData.digitalProofRequired && jobData.hardcopyProofRequired ? (
              <>{digitalHardcopyProofRender()}</>
            ) : (
              <>{hardcopyProofRender()}</>
            )
          ) : (
            <></>
          )}

          <div
            className={`supplier-proof-card-approve ${
              !defaultFileList || !defaultFileList?.length
                ? 'supplier-proof-card-fileNone'
                : ''
            }`}
          >
            <div className="supplier-proof-card-approve-container">
              <div className="supplier-proof-card-approve-container-action">
                {showPrepareNextVersion && (
                  <RevxButton
                    type="primary"
                    className="approve-proof"
                    onClick={() => prepareNextVersion()}
                  >
                    {t('workspace.detail.supplierProof.prepareNextVersion')}
                  </RevxButton>
                )}
                <RevxButton
                  type="primary"
                  className="approval-btn"
                  onClick={() => requestApproval()}
                  disabled={showRequestApproval.DefControl || isUploading}
                  hidden={!showRequestApproval.accessControl}
                >
                  {t('workspace.detail.supplierProof.requestApproval')}
                </RevxButton>
              </div>
            </div>
            {(jobData.participants.includes(JobParticipantEnum.CUSTOMER) ||
              jobData.participants.includes(JobParticipantEnum.PROXY) ||
              ([ProofStatusEnum.APPROVE, ProofStatusEnum.DECLINED].includes(
                choiceProof?.proofStatus || ProofStatusEnum.DRAFT
              ) &&
                choiceProof?.proofVersion !==
                  proofTabList[ProofTypeEnum.HARD_COPY]?.length &&
                choiceProof?.proofVersion !==
                  proofTabList[ProofTypeEnum.DIGITAL]?.length)) && (
              <div
                className={`buyer-proof-card-approve-container ${
                  choiceProof?.proofStatus !== ProofStatusEnum.DRAFT
                    ? 'supplier-proof-card-approve-container-box'
                    : ''
                }`}
                hidden={
                  choiceProof?.proofStatus === ProofStatusEnum.DRAFT ||
                  ((choiceProof?.proofVersion ===
                    proofTabList[ProofTypeEnum.HARD_COPY]?.length ||
                    choiceProof?.proofVersion ===
                      proofTabList[ProofTypeEnum.DIGITAL]?.length) &&
                    choiceProof?.proofStatus === ProofStatusEnum.DECLINED)
                }
              >
                <p hidden={proofActionStatus.choiceFileDisabled}>
                  {t('workspace.detail.proofs.approveProofTip')}
                </p>
                <div className="buyer-proof-card-approve-container-action">
                  {!proofActionStatus.showProofActionApprove && (
                    <RevxButton
                      className="no-approval"
                      disabled={proofActionStatus.choiceFileDisabled}
                      onClick={(e) => openNotes(e)}
                      hidden={proofActionStatus.showProofActionApprove}
                    >
                      {t('workspace.detail.proofs.noApprovalButton')}
                    </RevxButton>
                  )}
                  {!proofActionStatus.showProofActionDecline && (
                    <RevxButton
                      type="primary"
                      className="approve-proof"
                      disabled={proofActionStatus.choiceFileDisabled}
                      onClick={() => approveHandle()}
                    >
                      {proofActionStatus.choiceFileDisabled
                        ? t('workspace.detail.proofs.approvedButton')
                        : t('workspace.detail.proofs.approveProofButton')}
                    </RevxButton>
                  )}
                </div>
              </div>
            )}
            {choiceProof && viewNotes && (
              <CommonProofNotes
                ref={commonNoteRef}
                choiceProof={choiceProof}
                defaultProofList={defaultProofList}
                isDeniedApprovalModal={isDeniedApprovalModal}
                deniedApprovalHandler={declineHandle}
                hideAction={
                  showAddNoteBtn() ||
                  ![
                    JobWorkflowStatusEnum.PREPARE_PROOF,
                    JobWorkflowStatusEnum.PROOF_AWAIT_APPROVAL,
                    JobWorkflowStatusEnum.PROOF_NEED_REVISION,
                  ].includes(jobData.workflowStatus)
                }
              />
            )}
          </div>
        </RevxSkeleton>
      ) : (
        <div className="buyer-proof-card-approve-title">No proof required.</div>
      )}
    </div>
  );
};

export default SupplierProof;
